'use client';

import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WebViewScreen } from '@diritto/common/app/webview-header.constant';
import { AgeRestriction, CategoryType, ContentStatus, UsageType } from '@diritto/common/content/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ContentSelectType } from 'app-components/contents/editor/ContentSelectType';
import { EditContent } from 'app-components/contents/editor/EditContent';
import { EditContestContent } from 'app-components/contents/editor/EditContestContent';
import { getInitialContentData } from 'app/(root)/studio/contents/add/AddContentPage';
import { ButtonSize } from 'constants/button';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useContentContestMutation } from 'queries/contests/useContentContestMutation';
import { useCreateContentMutation } from 'queries/studio/useCreateContentMutation';
import { Button } from 'ui-components/common/Button';
import { FullPageLoading } from 'ui-components/common/Loading';
import { DirittoStudioLayout } from 'ui-components/layout/DirittoStudioLayout';
import { useInAppHeader } from 'ui-hooks/useInAppHeader';
import { useInAppStatusBar } from 'ui-hooks/useInAppStatusBar';
import {
  convertToEditContentDto,
  EditContentData,
  EditContestContentData,
  validateEditContent,
} from 'utils/editContent';
import { showAlertModal, showToast } from 'utils/modal';
import { SentryService } from 'utils/SentryService';
import { FormikType } from 'utils/types/formik';

export enum ContentType {
  Normal = 'Normal',
  Contest = 'Contest',
}

export const ContentTypeList = [ContentType.Normal, ContentType.Contest];
export const ContentTypeString = {
  [ContentType.Normal]: '디리토 연재',
  [ContentType.Contest]: '2026 제4회 픽앤츄',
};

/* 픽앤츄 작품 등록 페이지: 공모전 작품 등록 기간에만 사용 합니다. */
/* 작품 등록시, 공모전 선택하는 경우 분기가 됩니다. */
export const AddContestContentPage = ({ contestId }: { contestId: number }) => {
  const searchParams = useSearchParams();
  useInAppHeader({ screen: WebViewScreen.MyContentAdd, hideHeader: true });
  useInAppStatusBar(WebViewScreen.MyContentAdd);
  const [contentType, setContentType] = useState(
    searchParams?.get('type') === ContentType.Contest ? ContentType.Contest : ContentType.Normal,
  );

  const { tracker } = useTrackingState();
  useEffect(() => {
    tracker.track(TrackingEventType.ViewStudioContentAdd);
  }, [tracker]);

  const router = useRouter();
  const { mutateAsync: createContentMutate } = useCreateContentMutation();
  const { mutateAsync: createContentContestMutate } = useContentContestMutation();
  const isRequesting = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialValues = useMemo(
    () =>
      ({
        ...getInitialContentData(),
        status: ContentStatus.Private,
      }) as EditContestContentData,
    [],
  );

  const handleSave = useCallback(
    async (content: EditContestContentData) => {
      try {
        if (isRequesting.current) {
          return;
        }

        isRequesting.current = true;
        setIsLoading(true);

        const validateResult = validateEditContent(content);
        if (validateResult) {
          showToast(validateResult);
          return;
        }

        let data;
        if (contentType === ContentType.Normal) {
          data = await createContentMutate(convertToEditContentDto(content));
        } else if (contentType === ContentType.Contest) {
          data = await createContentContestMutate({
            contestId,
            payload: {
              contentEditDto: convertToEditContentDto(content),
            },
          });
        }
        if (!data) {
          throw new Error('Failed to create content');
        }

        tracker.track(TrackingEventType.CreateContent, { contentId: data.content.id });
        showToast('작품이 등록되었어요.\n반영 시 5분까지 소요될 수 있어요.');

        const url =
          contentType === ContentType.Normal
            ? `/studio/contents/${data.content.id}`
            : `/studio/contests/${contestId}/contents/${data.content.id}`;
        router.replace(url);
      } catch (e) {
        SentryService.captureException(e);
        showAlertModal('등록에 실패했어요. ;ㅅ;');
      } finally {
        isRequesting.current = false;
        setIsLoading(false);
      }
    },
    [contentType, createContentContestMutate, createContentMutate, router, tracker],
  );

  const formik = useFormik<EditContestContentData>({
    initialValues,
    onSubmit: handleSave,
  });

  const handleChangeContentType = useCallback(
    (event: any) => {
      if (event.target.value === ContentType.Contest) {
        formik.setFieldValue('category', CategoryType.BL);
        formik.setFieldValue('ageRestriction', AgeRestriction.All);
        formik.setFieldValue('isFancyShareEnabled', UsageType.Using);
        formik.setFieldValue('isProposalWanted', false);
        formik.setFieldValue('isMarketingAgreed', true);
        formik.setFieldValue('status', ContentStatus.Private);
      }
      setContentType(event.target.value);
    },
    [setContentType, formik],
  );

  return (
    <DirittoStudioLayout>
      <div className="pb-[100px] w-full relative flex flex-col items-center">
        <div className="w-full md:w-[1024px]">
          <div className="flex justify-between items-center p-4">
            <div className="text-lg font-bold">새 작품</div>
            <Button
              label="작품 등록"
              onClick={() => formik.handleSubmit()}
              size={ButtonSize.XSmall}
              buttonTypography={Typography.XXXSmall}
            />
          </div>
          <div className="p-4 font-xs font-regular">
            <StyledSubject>작품 분류</StyledSubject>
            <ContentSelectType
              id="contentType"
              list={ContentTypeList}
              stringMap={ContentTypeString}
              defaultValue={contentType}
              onChange={handleChangeContentType}
            />
          </div>
          {contentType === ContentType.Normal ? (
            <EditContent
              formik={formik as unknown as FormikType<EditContentData>}
              isLoadingSubmit={isLoading}
              isChanged
            />
          ) : (
            <EditContestContent
              formik={formik as FormikType<EditContestContentData>}
              isLoadingSubmit={isLoading}
              isChanged
            />
          )}
          {isLoading && <FullPageLoading />}
        </div>
      </div>
    </DirittoStudioLayout>
  );
};

const StyledSubject = styled.p`
  display: flex;
  align-items: center;
  margin: 32px 0 8px 0;
  color: ${Color.Gray600};
  font-weight: ${FontWeight.Medium};
  gap: 4px;
  ${Typography.XXSmall};

  &:first-of-type {
    margin-top: 0;
  }
`;
